import { Code, ConnectError } from "@bufbuild/connect";
import { LegacyPortalPurchaseHistoryService } from "@spwn-portal/grpc";
import { useSelector } from "store";
import useSWRImmutable from "swr/immutable";
import { useConnectClient } from "utility/connectWeb";

import type { LegacyPortalPurchaseHistoryOrder } from "@spwn-portal/grpc";

type Input = {
  /** falseの間は取得を始めない。取得元の切り替え中に二重取得しないため */
  enabled: boolean;
  /** 画面に出す注文番号。取得元（SPWN/Shopify）はAPIがこの値から判定する */
  orderId: string;
};

type Output = {
  error: unknown;
  isLoading: boolean;
  /** 本人の注文として存在しない。通信エラー等と区別し、この場合だけ一覧へ戻す */
  isNotFound: boolean;
  order: LegacyPortalPurchaseHistoryOrder | null;
};

const isNotFoundError = (error: unknown): boolean =>
  error instanceof ConnectError && error.code === Code.NotFound;

export const useGetPurchaseHistoryOrder = (input: Input): Output => {
  const { enabled, orderId } = input;
  const uid = useSelector((state) => state.auth.user.uid);
  const client = useConnectClient(LegacyPortalPurchaseHistoryService);

  // ログアウトしてもSWRキャッシュは消えない。別のユーザーが同じURLを開いたとき前のユーザーの注文を出さないよう、uidをキーに含める
  const { data, error, isLoading } = useSWRImmutable<
    LegacyPortalPurchaseHistoryOrder | null,
    unknown
  >(
    enabled && orderId !== "" && uid !== ""
      ? ["purchaseHistoryOrder", uid, orderId]
      : null,
    async () => {
      const response = await client.getPurchaseHistoryOrder({ orderId });
      return response.order ?? null;
    }
  );

  return {
    error: isNotFoundError(error) ? undefined : error,
    isLoading,
    isNotFound: isNotFoundError(error),
    order: data ?? null,
  };
};
